import { useMemo } from 'react';
import { useAuth } from './useAuth';
import { useAuthQuery } from './useAuthQuery';
import { logger } from '@/lib/logger';
import React from 'react';

// Enhanced permission hook with project-aware caching
const useOptimizedPermissionCheck = (permissionCode: string, projectId?: string) => {
  const { user } = useAuth();

  return useAuthQuery<boolean>({
    queryKey: ['permissions', user?.id, projectId, permissionCode],
    queryFn: async () => {
      const { checkUserPermission } = await import('@/lib/queries/authQueries');
      return checkUserPermission(user?.id || '', permissionCode);
    },
    requiresAuth: true,
    requiresSession: true,
    enabled: !!permissionCode && !!user?.id,
    staleTime: 60 * 1000,
    gcTime: 30 * 1000,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });
};

// 🪦 PR-G3: permissionCaching flag retired — individual queries were the live prod path.
export const useProcurementAccess = (projectId?: string) => {
  return useOptimizedPermissionCheck('PROCUREMENT_ACCESS', projectId);
};

export const useProcurementViewPermissions = (projectId?: string) => {
  const canViewItems = useOptimizedPermissionCheck('PROCUREMENT_ITEMS_VIEW', projectId);

  return useMemo(
    () => ({
      viewItems: canViewItems.data || false,
      isLoading: canViewItems.isLoading,
    }),
    [canViewItems.data, canViewItems.isLoading]
  );
};

export const useProcurementEditPermissions = (projectId?: string) => {
  const canEditItems = useOptimizedPermissionCheck('PROCUREMENT_ITEMS_EDIT', projectId);

  return useMemo(
    () => ({
      editItems: canEditItems.data || false,
      isLoading: canEditItems.isLoading,
    }),
    [canEditItems.data, canEditItems.isLoading]
  );
};

export const useProcurementManagePermissions = (projectId?: string) => {
  const canManageContractors = useOptimizedPermissionCheck('PROCUREMENT_MANAGE_CONTRACTORS', projectId);

  return useMemo(
    () => ({
      manageContractors: canManageContractors.data || false,
      isLoading: canManageContractors.isLoading,
    }),
    [canManageContractors.data, canManageContractors.isLoading]
  );
};

export const useProcurementOrderPermissions = (projectId?: string) => {
  const canCreate = useOptimizedPermissionCheck('PROCUREMENT_ORDER_CREATE', projectId);
  const canEdit = useOptimizedPermissionCheck('PROCUREMENT_ORDER_EDIT', projectId);
  const canDelete = useOptimizedPermissionCheck('PROCUREMENT_DELETE_ORDER', projectId);

  return useMemo(
    () => ({
      createOrder: canCreate.data || false,
      editOrder: canEdit.data || false,
      deleteOrder: canDelete.data || false,
      isLoading: canCreate.isLoading || canEdit.isLoading || canDelete.isLoading,
    }),
    [
      canCreate.data,
      canEdit.data,
      canDelete.data,
      canCreate.isLoading,
      canEdit.isLoading,
      canDelete.isLoading,
    ]
  );
};

export const useProcurementPaymentPermissions = (projectId?: string) => {
  const canCreateMilestone = useOptimizedPermissionCheck('SUBCONS_PAYMENT_MANAGE', projectId);
  const canEditMilestone = useOptimizedPermissionCheck('SUBCONS_PAYMENT_MANAGE', projectId);
  const canDeleteMilestone = useOptimizedPermissionCheck('SUBCONS_PAYMENT_MANAGE', projectId);
  const canToggleStatus = useOptimizedPermissionCheck('PROCUREMENT_TOGGLE_PAYMENT_STATUS', projectId);
  const canLockMilestone = useOptimizedPermissionCheck('SUBCONS_PAYMENT_MANAGE', projectId);

  const result = useMemo(
    () => ({
      createMilestone: canCreateMilestone.data || false,
      editMilestone: canEditMilestone.data || false,
      deleteMilestone: canDeleteMilestone.data || false,
      togglePaymentStatus: canToggleStatus.data || false,
      lockMilestone: canLockMilestone.data || false,
      isLoading:
        canCreateMilestone.isLoading ||
        canEditMilestone.isLoading ||
        canDeleteMilestone.isLoading ||
        canToggleStatus.isLoading ||
        canLockMilestone.isLoading,
    }),
    [
      canCreateMilestone.data,
      canEditMilestone.data,
      canDeleteMilestone.data,
      canToggleStatus.data,
      canLockMilestone.data,
      canCreateMilestone.isLoading,
      canEditMilestone.isLoading,
      canDeleteMilestone.isLoading,
      canToggleStatus.isLoading,
      canLockMilestone.isLoading,
    ]
  );

  React.useEffect(() => {
    if (import.meta.env.DEV) {
      logger.debug('PROCUREMENT', 'Payment Permissions Check', {
        projectId,
        permissions: result,
        rawData: {
          canCreateMilestone: canCreateMilestone.data,
          canToggleStatus: canToggleStatus.data,
          canLockMilestone: canLockMilestone.data,
        },
      });
    }
  }, [projectId, result, canCreateMilestone.data, canToggleStatus.data, canLockMilestone.data]);

  return result;
};
